// src/components/properties/property-view/components/FilterTray.tsx
import { useState } from 'react';
import { Lock, ChevronDown, ChevronUp, X } from 'lucide-react';

import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { FilterChip } from '../utils/buildFilterChips';

export interface LockedStatusChip {
  label: string;
  statuses: { value: string; label: string }[];
  viewName: string;
}

export interface InfoChip {
  id: string;
  label: string;
  value: string;
  /** Describes a filter the view applies implicitly (e.g. a backend default
   *  window) rather than one the user picked, so there is nothing in filter
   *  state to clear — but the user may still want it out of the way.
   *  Optional: dismisses the chip when provided; omit for a chip with
   *  nothing sensible to dismiss to. */
  onDismiss?: () => void;
}

export interface FilterTrayProps {
  chips: FilterChip[];
  lockedChip?: LockedStatusChip | null;
  infoChips?: InfoChip[];
  onClearAll: () => void;
}

const chipShell =
  'inline-flex h-8 items-center gap-2 rounded-full border border-primary/20 bg-primary/5 pl-3 pr-1.5 text-sm text-foreground';
const chipLabel =
  'text-[11px] font-semibold uppercase tracking-wide text-primary';

const LockedChip = ({ locked }: { locked: LockedStatusChip }) => {
  const [open, setOpen] = useState(false);
  const [first, ...rest] = locked.statuses;
  if (!first) return null;

  if (rest.length === 0) {
    return (
      <span
        className={`${chipShell} pr-3`}
        data-testid="locked-status-chip"
      >
        <Lock className="h-3.5 w-3.5 text-primary" aria-hidden />
        <span className={chipLabel}>{locked.label}</span>
        <span className="font-medium">{first.label}</span>
      </span>
    );
  }

  return (
    <span className={`${chipShell} pr-1`} data-testid="locked-status-chip">
      <Lock className="h-3.5 w-3.5 text-primary" aria-hidden />
      <span className={chipLabel}>{locked.label}</span>
      <span className="font-medium">
        {first.label} <span className="font-semibold text-primary" aria-label={`+${rest.length} more`}>+{rest.length}</span>
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="See all statuses"
            aria-expanded={open}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full text-primary hover:bg-primary/10"
          >
            {open ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-60 p-2">
          <div className="flex items-center gap-1.5 border-b border-border px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <Lock className="h-3 w-3" aria-hidden />
            Locked by {locked.viewName}
          </div>
          <ul className="pt-1.5">
            {locked.statuses.map((s) => (
              <li
                key={s.value}
                className="flex items-center gap-2 px-1 py-1.5 text-sm text-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
                {s.label}
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </span>
  );
};

const FilterTray = ({ chips, lockedChip, infoChips = [], onClearAll }: FilterTrayProps) => {
  if (!lockedChip && infoChips.length === 0 && chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2.5 pb-0 pt-3">
      {lockedChip && <LockedChip locked={lockedChip} />}
      {infoChips.map((chip) => (
        <span
          key={chip.id}
          className={chip.onDismiss ? chipShell : `${chipShell} pr-3`}
          data-testid="info-filter-chip"
        >
          <span className={chipLabel}>{chip.label}</span>
          <span className="font-medium">{chip.value}</span>
          {chip.onDismiss && (
            <button
              type="button"
              aria-label={`Dismiss ${chip.label}`}
              onClick={chip.onDismiss}
              className="inline-flex h-5 w-5 items-center justify-center rounded-full text-primary hover:bg-primary/10"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </span>
      ))}
      {chips.map((chip) => (
        <span key={chip.id} className={chipShell}>
          <span className={chipLabel}>{chip.label}</span>
          <span className="font-medium">{chip.value}</span>
          <button
            type="button"
            aria-label={`Remove ${chip.label}`}
            onClick={chip.onRemove}
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-primary hover:bg-primary/10"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      ))}
      {chips.length > 0 && (
        <button
          type="button"
          onClick={onClearAll}
          className="px-1 text-sm text-muted-foreground hover:text-primary hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
};

export default FilterTray;
